
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/sonner';
import { 
  Settings, 
  Save, 
  RotateCcw, 
  AlertTriangle,
  CheckCircle,
  Globe,
  RefreshCw
} from 'lucide-react';
import { useProxyConfig } from '@/contexts/ProxyConfigContext';

interface ProxyVariable {
  key: string;
  value: string;
  enabled: boolean;
  description: string;
  service: string;
  type: 'url' | 'enabled' | 'timeout';
}

interface ProxyConfig {
  [key: string]: ProxyVariable;
}

export const EnvironmentVariablesEditor: React.FC = () => {
  const { config: proxyConfigContext, loading: contextLoading, forceReload, updateConfig } = useProxyConfig();
  const [config, setConfig] = useState<ProxyConfig>({});
  const [originalConfig, setOriginalConfig] = useState<ProxyConfig>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Определяем структуру переменных окружения
  const proxyVariables: ProxyVariable[] = [
    // Supabase
    { key: 'VITE_SUPABASE_PROXY_ENABLED', value: 'false', enabled: false, description: 'Включить прокси для Supabase API', service: 'supabase', type: 'enabled' },
    { key: 'VITE_SUPABASE_PROXY_URL', value: '', enabled: false, description: 'URL прокси сервера для Supabase', service: 'supabase', type: 'url' },
    { key: 'VITE_SUPABASE_PROXY_TIMEOUT', value: '30000', enabled: false, description: 'Таймаут для Supabase прокси (мс)', service: 'supabase', type: 'timeout' },
    
    // Supabase Storage
    { key: 'VITE_SUPABASE_STORAGE_PROXY_ENABLED', value: 'false', enabled: false, description: 'Включить прокси для Supabase Storage', service: 'supabaseStorage', type: 'enabled' },
    { key: 'VITE_SUPABASE_STORAGE_PROXY_URL', value: '', enabled: false, description: 'URL прокси сервера для Supabase Storage', service: 'supabaseStorage', type: 'url' },
    { key: 'VITE_SUPABASE_STORAGE_PROXY_TIMEOUT', value: '30000', enabled: false, description: 'Таймаут для Supabase Storage прокси (мс)', service: 'supabaseStorage', type: 'timeout' },
    
    // Google Maps
    { key: 'VITE_GOOGLE_MAPS_PROXY_ENABLED', value: 'false', enabled: false, description: 'Включить прокси для Google Maps', service: 'googleMaps', type: 'enabled' },
    { key: 'VITE_GOOGLE_MAPS_PROXY_URL', value: '', enabled: false, description: 'URL прокси сервера для Google Maps', service: 'googleMaps', type: 'url' },
    { key: 'VITE_GOOGLE_MAPS_PROXY_TIMEOUT', value: '15000', enabled: false, description: 'Таймаут для Google Maps прокси (мс)', service: 'googleMaps', type: 'timeout' },
    
    // Geocoding
    { key: 'VITE_GEOCODING_PROXY_ENABLED', value: 'false', enabled: false, description: 'Включить прокси для Geocoding', service: 'geocoding', type: 'enabled' },
    { key: 'VITE_GEOCODING_PROXY_URL', value: '', enabled: false, description: 'URL прокси сервера для Geocoding', service: 'geocoding', type: 'url' },
    { key: 'VITE_GEOCODING_PROXY_TIMEOUT', value: '15000', enabled: false, description: 'Таймаут для Geocoding прокси (мс)', service: 'geocoding', type: 'timeout' },
    
    // Make Webhooks
    { key: 'VITE_MAKE_WEBHOOKS_PROXY_ENABLED', value: 'false', enabled: false, description: 'Включить прокси для Make.com Webhooks', service: 'makeWebhooks', type: 'enabled' },
    { key: 'VITE_MAKE_WEBHOOKS_PROXY_URL', value: '', enabled: false, description: 'URL прокси сервера для Make.com', service: 'makeWebhooks', type: 'url' },
    { key: 'VITE_MAKE_WEBHOOKS_PROXY_TIMEOUT', value: '10000', enabled: false, description: 'Таймаут для Make.com прокси (мс)', service: 'makeWebhooks', type: 'timeout' },
    
    // General Webhooks
    { key: 'VITE_WEBHOOKS_PROXY_ENABLED', value: 'false', enabled: false, description: 'Включить прокси для общих Webhooks', service: 'webhooks', type: 'enabled' },
    { key: 'VITE_WEBHOOKS_PROXY_URL', value: '', enabled: false, description: 'URL прокси сервера для Webhooks', service: 'webhooks', type: 'url' },
    { key: 'VITE_WEBHOOKS_PROXY_TIMEOUT', value: '10000', enabled: false, description: 'Таймаут для Webhooks прокси (мс)', service: 'webhooks', type: 'timeout' },
    
    // N8N Chat
    { key: 'VITE_N8N_WEBHOOK_URL', value: '', enabled: true, description: 'URL вебхука для N8N чата', service: 'n8n', type: 'url' },
  ];

  // Маппинг сервисов к ключам enabled переменных
  const serviceToEnabledKey: Record<string, string> = {
    'supabase': 'VITE_SUPABASE_PROXY_ENABLED',
    'supabaseStorage': 'VITE_SUPABASE_STORAGE_PROXY_ENABLED',
    'googleMaps': 'VITE_GOOGLE_MAPS_PROXY_ENABLED',
    'geocoding': 'VITE_GEOCODING_PROXY_ENABLED',
    'makeWebhooks': 'VITE_MAKE_WEBHOOKS_PROXY_ENABLED',
    'webhooks': 'VITE_WEBHOOKS_PROXY_ENABLED'
  };

  // Функция для проверки, включен ли прокси для сервиса
  const isProxyEnabledForService = (service: string): boolean => {
    const enabledKey = serviceToEnabledKey[service];
    if (!enabledKey || !config[enabledKey]) return false;
    return config[enabledKey].enabled === true;
  };

  // Загрузка текущих значений переменных из контекста
  const loadCurrentConfig = () => {
    setLoading(true);
    try {
      const newConfig: ProxyConfig = {};
      
      proxyVariables.forEach(variable => {
        const currentValue = proxyConfigContext[variable.key] || variable.value;
        newConfig[variable.key] = {
          ...variable,
          value: currentValue,
          enabled: variable.type === 'enabled' ? currentValue === 'true' : true
        };
      });
      
      setConfig(newConfig);
      setOriginalConfig({ ...newConfig });
      console.log('📝 Конфигурация редактора загружена:', Object.keys(newConfig));
    } finally {
      setLoading(false);
    }
  };

  // Исправленная функция сохранения изменений
  const saveChanges = async () => {
    setSaving(true);
    try {
      const variables: Record<string, string> = {};
      
      // Важно: правильно формируем переменные для отправки
      Object.values(config).forEach(variable => {
        if (variable.type === 'enabled') {
          // Для переменных типа enabled всегда сохраняем строковое значение
          variables[variable.key] = variable.enabled ? 'true' : 'false';
        } else {
          // Для остальных переменных сохраняем их строковое значение
          variables[variable.key] = variable.value;
        }
      });

      console.log('💾 Сохраняем переменные с исправленной логикой:', variables);

      const success = await updateConfig(variables);

      if (success) {
        setOriginalConfig({ ...config });
        setHasChanges(false);
        toast.success('Переменные окружения успешно сохранены и применены');
        
        // Принудительно перезагружаем конфигурацию для отображения актуального состояния
        setTimeout(async () => {
          await forceReload();
          loadCurrentConfig();
        }, 1000);
      } else {
        toast.error('Ошибка при сохранении переменных окружения');
      }
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      toast.error('Ошибка при сохранении переменных окружения');
    } finally {
      setSaving(false);
    }
  };

  // Отмена изменений
  const resetChanges = () => {
    setConfig({ ...originalConfig });
    setHasChanges(false);
    toast.info('Изменения отменены');
  };

  // Принудительное обновление конфигурации
  const handleForceReload = async () => {
    console.log('🔄 Принудительное обновление конфигурации редактора...');
    toast.info('Обновление конфигурации...');
    
    try {
      await forceReload();
      loadCurrentConfig();
      toast.success('Конфигурация обновлена');
    } catch (error) {
      console.error('Ошибка при обновлении:', error);
      toast.error('Ошибка при обновлении конфигурации');
    }
  };

  // Исправленная обработка изменений
  const handleVariableChange = (key: string, value: string | boolean) => {
    setConfig(prev => {
      const updatedConfig = {
        ...prev,
        [key]: {
          ...prev[key],
          ...(typeof value === 'boolean' 
            ? { 
                enabled: value, 
                value: value ? 'true' : 'false' 
              }
            : { value: value as string }
          )
        }
      };
      
      console.log(`🔧 Изменение переменной ${key}:`, {
        oldValue: prev[key]?.value,
        newValue: updatedConfig[key].value,
        enabled: updatedConfig[key].enabled
      });
      
      return updatedConfig;
    });
    setHasChanges(true);
  };

  // Валидация URL
  const validateUrl = (url: string): boolean => {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    loadCurrentConfig();
  }, [proxyConfigContext]);

  if (loading || contextLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tuktuk-teal mx-auto mb-4"></div>
            <p className="text-muted-foreground">Загрузка переменных окружения...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Группируем переменные по сервисам
  const groupedVariables = proxyVariables.reduce((acc, variable) => {
    if (!acc[variable.service]) {
      acc[variable.service] = [];
    }
    acc[variable.service].push(variable.key);
    return acc;
  }, {} as Record<string, string[]>);

  const serviceNames: Record<string, string> = {
    supabase: 'Supabase API',
    supabaseStorage: 'Supabase Storage',
    googleMaps: 'Google Maps',
    geocoding: 'Geocoding',
    makeWebhooks: 'Make.com Webhooks',
    webhooks: 'General Webhooks',
    n8n: 'N8N Chat'
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center">
            <Settings className="h-5 w-5 mr-2 text-tuktuk-teal" />
            Редактор переменных окружения
          </CardTitle>
          <Button
            onClick={handleForceReload}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Обновить
          </Button>
        </div>
        {hasChanges && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              У вас есть несохраненные изменения. Настройки будут применены сразу после сохранения.
            </AlertDescription>
          </Alert>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.entries(groupedVariables).map(([service, variableKeys]) => (
          <div key={service} className="space-y-4">
            <div className="flex items-center space-x-2">
              <Globe className="h-4 w-4 text-tuktuk-teal" />
              <h3 className="text-lg font-medium">{serviceNames[service]}</h3>
              <Badge variant="outline">
                {isProxyEnabledForService(service) ? 'Включен' : 'Отключен'}
              </Badge>
            </div>
            
            <div className="grid gap-4 pl-6">
              {variableKeys.map(key => {
                const variable = config[key];
                if (!variable) return null;

                if (variable.type === 'enabled') {
                  return (
                    <div key={key} className="flex items-center justify-between space-x-4">
                      <div className="flex-1">
                        <Label className="text-sm font-medium">{variable.description}</Label>
                        <div className="text-xs text-muted-foreground">{key}</div>
                        <div className="text-xs text-blue-600">
                          Текущее значение: {variable.enabled ? 'включен' : 'отключен'}
                        </div>
                      </div>
                      <Switch
                        checked={variable.enabled}
                        onCheckedChange={(checked) => handleVariableChange(key, checked)}
                      />
                    </div>
                  );
                }

                const isUrlField = variable.type === 'url';
                const isValid = !isUrlField || validateUrl(variable.value);
                const shouldBeDisabled = isUrlField && !isProxyEnabledForService(variable.service);

                return (
                  <div key={key} className="space-y-2">
                    <Label className="text-sm font-medium">{variable.description}</Label>
                    <div className="text-xs text-muted-foreground mb-1">{key}</div>
                    <div className="flex items-center space-x-2">
                      <Input
                        value={variable.value}
                        onChange={(e) => handleVariableChange(key, e.target.value)}
                        placeholder={isUrlField ? 'https://your-proxy.com' : variable.value}
                        className={!isValid ? 'border-red-500' : ''}
                        disabled={shouldBeDisabled}
                      />
                      {!isValid && (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                    {!isValid && (
                      <div className="text-xs text-red-500">
                        Введите корректный URL (например: https://your-proxy.com)
                      </div>
                    )}
                    {shouldBeDisabled && (
                      <div className="text-xs text-yellow-600">
                        Поле отключено. Включите прокси для {serviceNames[variable.service]} чтобы редактировать URL.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <Separator />
          </div>
        ))}

        <div className="flex items-center justify-between pt-4">
          <Alert className="flex-1 mr-4">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              Настройки применяются сразу после сохранения без перезапуска приложения.
            </AlertDescription>
          </Alert>
          
          <div className="flex space-x-2">
            <Button
              onClick={resetChanges}
              variant="outline"
              disabled={!hasChanges || saving}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Отменить
            </Button>
            <Button
              onClick={saveChanges}
              disabled={!hasChanges || saving}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Сохраняется...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Сохранить
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
